import React, { useState } from 'react';
import { 
  Box, TextField, Button, Typography, Container, Grid, Card, CardContent,
  Alert, Snackbar, InputAdornment, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Styled card with static white border
const GlassmorphicCard = styled(Card)(({ theme }) => ({
  background: 'rgba(1, 22, 15, 0.3)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  position: 'relative',
  padding: theme.spacing(3),
  maxWidth: '450px',
  width: '100%',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  border: '2px solid rgba(255, 255, 255, 0.15)'
}));

// Gradient text for button
const GradientText = styled('span')({
  background: 'linear-gradient(45deg, #01FEAD, #0077FF)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 'bold',
});

export default function LoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const validate = () => {
    let tempErrors = { username: '', password: '' };
    let isValid = true;

    if (!formData.username) {
      tempErrors.username = 'Username is required';
      isValid = false;
    } else if (formData.username.length < 3) {
      tempErrors.username = 'Username must be at least 3 characters';
      isValid = false;
    }

    if (!formData.password) {
      tempErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      tempErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    } else if (!/(?=.*[0-9])(?=.*[a-zA-Z])/.test(formData.password)) {
      tempErrors.password = 'Password must contain both letters and numbers';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validate()) {
      navigate('/profile/yash-shah');
    } else {
      setAlertMessage('Please fix the errors in the form');
      setAlertOpen(true);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
      '&:hover fieldset': { borderColor: '#01FEAD' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInputBase-input': { color: '#ffffff' },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, #000000 0%, #01160f 100%)`,
        padding: { xs: 2, sm: 4 },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* First blob - Green tint */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '5%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(1, 254, 173, 0.12) 0%, rgba(1, 22, 15, 0) 70%)',
          zIndex: 0,
          animation: 'blob1Move 25s infinite alternate'
        }}
      />
      
      {/* Second blob - Blue tint */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '8%',
          right: '10%',
          width: '45%',
          height: '35%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0, 119, 255, 0.1) 0%, rgba(1, 22, 15, 0) 70%)',
          zIndex: 0,
          animation: 'blob2Move 20s infinite alternate-reverse'
        }}
      />
      
      {/* Third blob - Purple tint */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '30%',
          left: '25%',
          width: '30%',
          height: '25%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(128, 0, 255, 0.08) 0%, rgba(1, 22, 15, 0) 75%)',
          zIndex: 0,
          animation: 'blob3Move 30s infinite alternate'
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <GlassmorphicCard>
              <CardContent>
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  align="center"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #01FEAD, #0077FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4
                  }}
                >
                  Login
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <TextField
                    name="username"
                    label="Username"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.username}
                    onChange={handleChange}
                    error={Boolean(errors.username)}
                    helperText={errors.username}
                    sx={textFieldSx}
                  />
                  
                  <TextField
                    name="password"
                    label="Password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 2,
                      height: '50px',
                      background: '#fff',
                      borderRadius: '8px',
                      transition: 'transform 0.3s ease',
                      color: '#FFFFFF',
                      '&:hover': {
                        background: '#fff',
                        transform: 'scale(1.02)',
                        boxShadow: '0 10px 20px rgba(1, 254, 173, 0.2)'
                      }
                    }}
                  >
                    <GradientText>LOGIN</GradientText>
                  </Button>
                </form>
              </CardContent>
            </GlassmorphicCard>
          </Grid>
        </Grid>
      </Container>
      
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setAlertOpen(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      
      <style jsx global>{`
        @keyframes blob1Move {
          0% { transform: translate(0, 0) scale(1); opacity: 0.12; }
          50% { transform: translate(5%, 10%) scale(1.1); opacity: 0.15; }
          100% { transform: translate(-5%, 5%) scale(0.95); opacity: 0.1; }
        }
        
        @keyframes blob2Move {
          0% { transform: translate(0, 0) scale(1); opacity: 0.1; }
          60% { transform: translate(-8%, -5%) scale(1.15); opacity: 0.13; }
          100% { transform: translate(5%, -10%) scale(0.9); opacity: 0.08; }
        }
        
        @keyframes blob3Move {
          0% { transform: translate(0, 0) scale(1); opacity: 0.08; }
          30% { transform: translate(10%, 5%) scale(1.2); opacity: 0.1; }
          70% { transform: translate(-5%, 15%) scale(0.9); opacity: 0.06; }
          100% { transform: translate(8%, -10%) scale(1.1); opacity: 0.09; }
        }
        
        body {
          margin: 0;
          padding: 0;
          font-family: 'Roboto', sans-serif;
        }
      `}</style>
    </Box>
  );
}